import { SupportedLocales, isValidLocale } from "@/utils/localization";

/**
 * Route definitions for Trello Portal
 * This is a subset of wac-routes.ts focused only on trello routes
 */
export type TrelloRouteWithVariant =
    | {
          locale: SupportedLocales;
          featureFlags: Record<string, boolean>;
          product?: string;
      } & (
          | { path: "trello"; variant: undefined; search: string }
          | { path: `trello/${string}`; variant: undefined; search: string }
      );

export const getFeatureFlagValues = (params: string[]) => {
    // set defaults for feature flags
    const featureFlags = {
        enableBundlesAfterSignup: false,
        enableCutSignUpStepsInHalf: false,
        provisionSecondPartyApps: false
    };
    params[0] && (params[0] = params[0].replace(/^\?(.+)/, "$1"));
    params.forEach((param) => {
        const [key, value] = param.split("=");
        switch (key) {
            case "provisionSecondPartyApps":
                if (value === "enableProvisionSecondPartyApps") {
                    featureFlags.provisionSecondPartyApps = true;
                }
                break;
        }
    });
    return featureFlags;
};

/**
 * Parse the browser location into a structured route object
 *
 * @param location - Browser Location object
 * @returns Parsed route with locale, path, variant, and additional metadata
 *
 * @example
 * const route = parseRoute(window.location);
 * // Returns: { path: "trello", locale: "en", variant: undefined, ... }
 */

export const parseRoute = (
    location: Location
): TrelloRouteWithVariant & {
    locale: SupportedLocales;
    featureFlags: object;
} => {
    // remove all leading slashes (server had 2 leading slashes, client has 1)
    const noSlashPrefixPath = location.pathname.replace(/^\/+/, "");
    if (noSlashPrefixPath === "index.html" || noSlashPrefixPath === "") {
        // case for the root/home page - redirect to trello
        return {
            locale: "en",
            path: "trello",
            variant: undefined,
            search: "",
            featureFlags: {}
        };
    }

    // check if the route has a locale prefix
    const potentialLocale = noSlashPrefixPath.split("/")[0] || "";
    const localeIsValid = isValidLocale(potentialLocale);

    // if there is a locale prefix, set the locale variable and remove the prefix from the path
    // otherwise, set the locale to the default (en) and leave the path as is
    const locale = localeIsValid ? potentialLocale : "en";
    const cleanedPath = (
        localeIsValid
            ? noSlashPrefixPath.slice(locale.length + 1)
            : noSlashPrefixPath
    ) as TrelloRouteWithVariant["path"];

    const params = location.search.split("&");

    // Extract variant from query params (e.g., ?cmsVariant=some-variant)
    const cmsVariantValue = params
        .filter((param) => param.includes("cmsVariant"))[0]
        ?.split("=")[1] as TrelloRouteWithVariant["variant"];

    const featureFlags = getFeatureFlagValues(params);

    return {
        path: cleanedPath,
        variant: cmsVariantValue,
        locale,
        search: location.search,
        featureFlags
    } as TrelloRouteWithVariant & {
        locale: SupportedLocales;
        featureFlags: object;
    };
};
